import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Home, ShieldCheck, Clock, FileText } from 'lucide-react';

const brandRed  = "#b22335";
const brandNavy = "#3D3B6D";

const faqStyles = {
  section: {
    backgroundColor: 'white',
    padding: '30px 0',
  },
  subtitleWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    color: '#2b4d8d',
    fontFamily: "'hauora-medium', sans-serif",
    
    fontSize: '15px',
    padding: '8px 25px',
    borderRadius: '50px',
    letterSpacing: '0.3px',
    boxShadow: '0 0 20px rgba(178, 35, 53, 0.25)',
    border: '1px solid rgba(178, 35, 53, 0.1)',
   
  },
  dot: {
    height: '8px',
    width: '8px',
    backgroundColor: brandRed,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '12px',
    flexShrink: 0,
  },
  heading: {
    fontFamily: "'hauora-medium', sans-serif",
    color: brandNavy,
    
  },
  headingRed: {
    color: brandRed,
  },
  questionText: {
    fontFamily: "'hauora-medium', sans-serif",
    fontSize: 'clamp(1rem, calc(1rem + 0.55vw), 1rem)',
    
    color: brandNavy,
    
  },
  answerText: {
    fontFamily: "'hauora-medium', sans-serif",
    fontSize: '15px',
    
    color: '#4d5663',
    padding: '0 24px 24px 64px',
  },
  btnBase: {
    width: '100%',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
  },
};

/* Responsive heading via CSS injection — avoids a separate file */
const ResponsiveStyle = () => (
  <style>{`
    
    .faq-section-wrap {
      background-color: white;
      padding: 30px 10px;
    }
    @media (max-width: 991px) {
      .faq-section-wrap { padding: 20px 10px; }
    }
    @media (max-width: 576px) {
      .faq-section-wrap { padding: 20px 10px; }
      .faq-answer-text  { padding: 0 16px 20px 16px !important; font-size: 15px !important; }
      .faq-question-btn { padding: 16px !important; }
      .faq-subtitle     { font-size: 13px !important; padding: 7px 18px !important; }
    }
  `}</style>
);

const Faq = () => {
  const [activeId, setActiveId] = useState(null);

  const faqs = [
    {
      id: 1,
      icon: <Home size={20} />,
      question: "Do you provide free roof inspections?",
      answer: "Absolutely! We offer free roof inspections and estimates to help homeowners make informed decisions. Our experts thoroughly assess your roof's condition, identify any issues, and provide a clear, honest recommendation — whether you need repairs or a full replacement."
    },
    {
      id: 2,
      icon: <ShieldCheck size={20} />,
      question: "How do I find the best roofing contractor near me in Columbus, Ohio?",
      answer: "Finding the best roofing contractor in Columbus, Ohio starts with checking licenses, insurance, and certifications to ensure legitimacy and safety. Look for companies with strong customer reviews, proven local experience, and a solid portfolio of completed projects. A reliable contractor will also offer transparent pricing, written estimates, and clear communication throughout the process. Choosing a local expert ensures they understand Ohio's weather conditions and building codes."
    },
    {
      id: 3,
      icon: <Clock size={20} />,
      question: "What roofing materials are best for Ohio weather?",
      answer: "In Columbus, Ohio, the best roofing materials are those that can withstand harsh winters, heavy rain, and strong winds. Asphalt shingles are a popular choice due to their affordability, durability, and ease of maintenance. Metal roofing is highly recommended for its long lifespan, energy efficiency, and superior resistance to extreme weather conditions. A professional contractor can help you choose the best material based on your budget and home style."
    },
    {
      id: 4,
      icon: <FileText size={20} />,
      question: "Do you offer emergency roof repair near me?",
      answer: "Yes! We provide 24/7 emergency roof repair services across Columbus, Ohio to protect your home when unexpected damage occurs. Whether it's due to storms, heavy winds, or sudden leaks, our rapid response team is ready to secure your property, prevent further damage, and restore your roof as quickly as possible."
    }
  ];

  return (
    <>
      <ResponsiveStyle />
      <div className="faq-section-wrap cs-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9}>

              {/* Section Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                {/* Subtitle pill */}
                <div className="cs-badge">
                  <span className="faq-subtitle" style={faqStyles.subtitleWrap}>
                    <span style={faqStyles.dot}></span>
                    Columbus Roofing FAQ
                  </span>
                </div>

                {/* Main heading */}
                <h2 className="cs-title cs-title-to-content" style={faqStyles.heading}>
                  Frequently Asked <span style={faqStyles.headingRed}>Questions.</span>
                </h2>
              </motion.div>

              {/* FAQ Items */}
              <div className="cs-content">
                {faqs.map((faq) => {
                  const isOpen = activeId === faq.id;
                  return (
                    <motion.div
                      key={faq.id}
                      initial={false}
                      animate={{ backgroundColor: isOpen ? '#fff' : 'rgba(255,255,255,0.6)' }}
                      className="mb-4 rounded-4 shadow-sm overflow-hidden"
                      style={{
                        border: '1px solid #eee',
                        borderLeft: `5px solid ${isOpen ? brandRed : brandNavy}`,
                        transition: 'border-left 0.3s ease',
                      }}
                    >
                      {/* Question Button */}
                      <button
                        className="faq-question-btn"
                        onClick={() => setActiveId(isOpen ? null : faq.id)}
                        style={faqStyles.btnBase}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <span style={{ color: isOpen ? brandRed : brandNavy, flexShrink: 0 }}>
                            {faq.icon}
                          </span>
                          <span style={faqStyles.questionText}>
                            {faq.question}
                          </span>
                        </div>
                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                          style={{ flexShrink: 0, marginLeft: '12px' }}
                        >
                          <ChevronDown color={isOpen ? brandRed : '#ccc'} />
                        </motion.div>
                      </button>

                      {/* Answer */}
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                          >
                            <div
                              className="faq-answer-text"
                              style={faqStyles.answerText}
                            >
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>

            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Faq;